import { memo, useRef, useState } from "react";
import { View } from "react-native";
import cursor from "src/constants/cursor";
import useClearingTimeouts from "src/hooks/useClearingTimeouts";
import useDevice from "src/hooks/useDevice";
import useKeyboardListener from "src/hooks/useKeyboardListener";
import useStyle from "src/hooks/useStyle";
import IconSearch from "src/icons/IconSearch";
import { color } from "src/styles";
import { radiusLarge, radiusSemiLarge } from "src/styles/radius";
import { paddingLarge, paddingMedium, paddingXL } from "src/styles/spacing";
import { AnimatePresence, Text, TextInput, TextInputHandler } from "src/swsh-native";
import OpacityView from "../OpacityView";
import Touchable from "../Touchable";
import { SearchProps } from "./types";

const closePadding = paddingLarge;
const closeSize = 32;
const textPadding = paddingXL;
const Search = ({
	placeholder: { Icon: IconPlaceholder = IconSearch, text: textPlaceholder = "Search" } = {},
	hasShortcuts = true,
	onFocus,
	onBlur,
	onChangeText,
	onDebouncedChangeText,
	style,
	...props
}: SearchProps) => {
	const textInputRef = useRef<TextInputHandler>(null);
	const { device } = useDevice();
	const { theme } = useStyle();
	const [hasText, setHasText] = useState(false);
	const [isFocused, setIsFocused] = useState(false);

	const borderRadius = device === "desktop" ? radiusLarge : radiusSemiLarge;

	const { createClearingTimeout } = useClearingTimeouts();
	useKeyboardListener(
		hasShortcuts
			? {
					"/": () => {
						// Slight delay so the / character doesn't get typed
						createClearingTimeout(() => textInputRef.current?.focus(), 20);
					},
					Escape: textInputRef.current?.blur,
				}
			: {},
	);

	return (
		<View
			style={[
				{
					flex: 1,
					maxHeight: device === "desktop" ? 56 : 48,
					width: 452,
					maxWidth: "90%",
					flexDirection: "row",
					alignItems: "center",
				},
				style,
			]}
		>
			<TextInput
				testID="SearchInput"
				{...props}
				ref={textInputRef}
				onFocus={(e) => {
					setIsFocused(true);
					onFocus?.(e);
				}}
				onBlur={(e) => {
					setIsFocused(false);
					onBlur?.(e);
				}}
				style={{
					width: "100%",
					height: "100%",
					borderRadius,
					backgroundColor: color.ButtonBackground[theme],
					overflow: "visible",
					paddingLeft: textPadding,
					// Leaving space to close overlay
					paddingRight: 2 * closePadding + closeSize,
				}}
				onChangeText={onChangeText}
				onDebouncedChangeText={(text, extra) => {
					onDebouncedChangeText?.(text, extra);
					if (hasText && !text) {
						setHasText(false);
					} else if (!hasText && text) {
						setHasText(true);
					}
				}}
			/>
			<AnimatePresence>
				{hasText && (
					<OpacityView
						duration={100}
						style={{
							position: "absolute",
							right: 0,
							alignSelf: "center",
							cursor: cursor.pointer,
						}}
					>
						<Touchable
							onPress={() => {
								textInputRef.current?.clear();
								setHasText(false);
								textInputRef.current?.focus();
								onChangeText?.("");
								onDebouncedChangeText?.("", {});
							}}
							containerStyle={{
								padding: closePadding,
								borderRadius,
							}}
						>
							<Text small color={color.InputPlaceholder[theme]}>
								Clear
							</Text>
						</Touchable>
					</OpacityView>
				)}
			</AnimatePresence>
			{!isFocused && !hasText && (
				<View
					onClick={() => textInputRef.current?.focus()}
					pointerEvents="none"
					style={{
						position: "absolute",
						flexDirection: "row",
						alignItems: "center",
						paddingLeft: textPadding,
					}}
				>
					{IconPlaceholder ? (
						<IconPlaceholder size={20} color={color.InputPlaceholder[theme]} />
					) : null}
					<Text
						color="InputPlaceholder"
						style={{
							marginLeft: paddingMedium,
						}}
					>
						{textPlaceholder}
					</Text>
				</View>
			)}
		</View>
	);
};
export default memo(Search);
